/**
 * EnergyBalanceChart - Stacked area chart showing 24-hour energy balance.
 *
 * Displays hourly energy flows including:
 *   - Load (consumption)
 *   - PV Generation
 *   - Wind Generation
 *   - Grid Import
 *   - Surplus (energy produced beyond local consumption)
 *   - Battery Charge/Discharge
 *
 * Features:
 *   - Building-level dropdown to filter by specific building or community total
 *   - Uses ApexCharts for responsive stacked area visualization
 *   - Mock data with realistic energy patterns
 *
 * Props:
 *   - scope: 'community' | buildingId - Filter data by building
 *   - onScopeChange: Callback when scope changes
 *   - data: Optional external data override (defaults to mock data)
 */

import React, { useMemo } from 'react'
import Chart from 'react-apexcharts'
import { Form } from 'react-bootstrap'

// Deterministic seeded RNG (avoids Math.random for reproducible charts)
function seededRng(seed) {
  let s = Math.abs(seed) || 1
  return () => { s = (s * 16807 + 12345) % 2147483647; return (s & 0x7fffffff) / 2147483647 }
}

// Mock buildings for dropdown
const MOCK_BUILDINGS = [
  { id: 'community', label: 'Community (Total)' },
  { id: 'building_a', label: 'Building A - Apartments' },
  { id: 'building_b', label: 'Building B - Office' },
  { id: 'building_c', label: 'Building C - School' },
]

// Generate reproducible 24-hour energy profile using seeded RNG
const generateMockData = (scope = 'community') => {
  const rng = seededRng(42)
  const hours = Array.from({ length: 24 }, (_, i) => i)

  // Community-level profiles (full scale)
  const communityProfile = {
    load: hours.map(h => {
      const nightBase = 2.5
      const morningPeak = h >= 6 && h <= 9 ? 3.0 + (8 - Math.abs(h - 7.5)) * 0.8 : 0
      const eveningPeak = h >= 17 && h <= 22 ? 4.0 + (5 - Math.abs(h - 19)) * 1.2 : 0
      const dayBase = h >= 9 && h <= 17 ? 3.5 : 0
      return nightBase + morningPeak + eveningPeak + dayBase + rng() * 0.5
    }),
    pv: hours.map(h => {
      if (h < 6 || h > 18) return 0
      const hourFromNoon = Math.abs(h - 12)
      return Math.max(0, 15 * Math.exp(-0.08 * hourFromNoon * hourFromNoon) - 0.5 + rng() * 0.3)
    }),
    wind: hours.map(() => rng() * 5 + 1),
  }

  // Building-specific profiles (scaled from community)
  const buildingProfiles = {
    building_a: {
      load: communityProfile.load.map(v => v * 0.4),
      pv: communityProfile.pv.map(v => v * 0.3),
      wind: [],
    },
    building_b: {
      load: hours.map(h => {
        if (h < 7 || h > 19) return 0.8 + rng() * 0.2
        return 2.5 + rng() * 0.5
      }),
      pv: communityProfile.pv.map(v => v * 0.25),
      wind: [],
    },
    building_c: {
      load: hours.map(h => {
        if (h < 7 || h > 16) return 0.5 + rng() * 0.2
        return 2.0 + rng() * 0.5
      }),
      pv: communityProfile.pv.map(v => v * 0.2),
      wind: [],
    },
  }

  const profile = scope === 'community' ? communityProfile : buildingProfiles[scope]
  if (!profile) return communityProfile // Fallback

  // Calculate grid and battery flows
  const totalGeneration = hours.map(
    (_, i) => (profile.pv[i] || 0) + (profile.wind[i] || 0)
  )
  const netBalance = hours.map((_, i) => totalGeneration[i] - profile.load[i])

  const gridImport = netBalance.map(b => (b < 0 ? Math.abs(b) : 0))
  const gridExport = netBalance.map(b => (b > 2 ? Math.min(b - 2, 3) : 0)) // Export excess above 2kW threshold

  // Battery: charges during excess PV (10am-3pm), discharges during evening peak (6pm-10pm)
  const batteryCharge = hours.map((h, i) => {
    if (h >= 10 && h <= 15 && netBalance[i] > 0) return Math.min(netBalance[i] * 0.4, 2)
    return 0
  })
  const batteryDischarge = hours.map((h, i) => {
    if (h >= 18 && h <= 22 && netBalance[i] < 0) return Math.min(Math.abs(netBalance[i]) * 0.3, 1.5)
    return 0
  })

  return {
    hours,
    load: profile.load,
    pv: profile.pv || [],
    wind: profile.wind || [],
    gridImport,
    gridExport,
    batteryCharge,
    batteryDischarge,
  }
}

/**
 * Build color array dynamically to match the series order.
 * Series order varies based on scope and available data.
 */
function buildSeriesColors(scope, chartData) {
  const colors = ['#C73E1D'] // Load - always first
  const hasFlexLoad = chartData.flexLoad?.length > 0 && chartData.flexLoad.some(v => v > 0)
  const hasPV = chartData.pv?.length > 0 && chartData.pv.some(v => v > 0)
  const hasCommAlloc = chartData.commAllocation?.length > 0 && chartData.commAllocation.some(v => v > 0)
  const hasWind = chartData.wind?.length > 0 && chartData.wind.some(v => v > 0)

  if (hasFlexLoad) colors.push('#E07850')      // Flexible Load - warm coral
  if (hasPV) colors.push('#FFB800')            // PV - yellow/solar
  if (hasCommAlloc && scope !== 'community') colors.push('#8E44AD') // Community allocation - purple
  if (hasWind) colors.push('#6DB1BF')          // Wind - cyan
  colors.push('#95A3A6')                       // Grid Import - gray
  colors.push('#27AE60')                       // Surplus - green
  if (scope === 'community') {
    colors.push('#2ECC71')                     // Battery Charge - green
    colors.push('#E67E22')                     // Battery Discharge - orange
  }
  return colors
}

function EnergyBalanceChart({ scope = 'community', onScopeChange, data, buildings = MOCK_BUILDINGS }) {
  // Use real optimizer data when provided; only fall back to synthetic for dashboard previews
  const chartData = useMemo(() => {
    if (data && data.load && data.load.length > 0) return data
    return generateMockData(scope)
  }, [data, scope])

  // ApexCharts configuration
  const chartOptions = {
    chart: {
      type: 'area',
      height: 350,
      stacked: false,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      animations: {
        enabled: true,
        speed: 800,
      },
    },
    colors: buildSeriesColors(scope, chartData),
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    fill: {
      type: 'solid',
      opacity: 0.6,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      fontFamily: 'var(--font-family-base)',
      markers: {
        width: 10,
        height: 10,
        radius: 2,
      },
      itemMargin: {
        horizontal: 12,
        vertical: 4,
      },
    },
    xaxis: {
      categories: chartData.hours,
      title: {
        text: 'Hour of Day',
        style: {
          fontSize: '12px',
          fontWeight: 500,
        },
      },
      labels: {
        style: {
          fontSize: '11px',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Power (kW)',
        style: {
          fontSize: '12px',
          fontWeight: 500,
        },
      },
      labels: {
        style: {
          fontSize: '11px',
        },
        formatter: (value) => value != null ? value.toFixed(1) : '',
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => value != null ? `${value.toFixed(2)} kW` : '',
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  }

  const hasFlexLoad = chartData.flexLoad?.length > 0 && chartData.flexLoad.some(v => v > 0)
  const hasPV = chartData.pv?.length > 0 && chartData.pv.some(v => v > 0)
  const hasCommAlloc = chartData.commAllocation?.length > 0 && chartData.commAllocation.some(v => v > 0)
  const hasWind = chartData.wind?.length > 0 && chartData.wind.some(v => v > 0)

  const series = [
    {
      name: 'Base Load',
      data: chartData.load,
      type: 'area',
    },
    ...(hasFlexLoad
      ? [{ name: 'Flexible Load', data: chartData.flexLoad, type: 'area' }]
      : []),
    ...(hasPV
      ? [{ name: scope === 'community' ? 'PV Generation' : 'Rooftop PV', data: chartData.pv, type: 'area' }]
      : []),
    ...(hasCommAlloc && scope !== 'community'
      ? [{ name: 'Community Generation Allocation', data: chartData.commAllocation, type: 'area' }]
      : []),
    ...(hasWind
      ? [{ name: 'Wind Generation', data: chartData.wind, type: 'area' }]
      : []),
    {
      name: 'Grid Import',
      data: chartData.gridImport,
      type: 'area',
    },
    {
      name: 'Surplus',
      data: chartData.gridExport,
      type: 'area',
    },
    ...(scope === 'community'
      ? [
          {
            name: 'Battery Charge',
            data: chartData.batteryCharge,
            type: 'area',
          },
          {
            name: 'Battery Discharge',
            data: chartData.batteryDischarge,
            type: 'area',
          },
        ]
      : []),
  ]

  return (
    <div>
      {/* Scope Dropdown */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-600)' }}>
          Select scope to view energy balance
        </div>
        <Form.Select
          value={scope}
          onChange={(e) => onScopeChange && onScopeChange(e.target.value)}
          style={{
            width: 'auto',
            minWidth: '220px',
            fontSize: 'var(--font-size-sm)',
            borderColor: 'var(--color-neutral-300)',
          }}
        >
          {buildings.map((building) => (
            <option key={building.id} value={building.id}>
              {building.label}
            </option>
          ))}
        </Form.Select>
      </div>

      {/* Chart */}
      <Chart options={chartOptions} series={series} type="area" height={350} />
    </div>
  )
}

export default EnergyBalanceChart
